import React, { useState, useEffect, useCallback } from 'react';
import Header from './Header';
import Hero from './Hero';
import Services from './Services';
import WhyPartner from './WhyPartner';
import Contact from './Contact';
import ImprintModal from './ImprintModal';
import PrivacyModal from './PrivacyModal';
import Footer from './Footer';
import { translations } from '../data/translations';

const LANGUAGE_STORAGE_KEY = 'chalhoub-trade-language';

// Detect browser language and return appropriate language code
const detectBrowserLanguage = () => {
  const browserLang = navigator.language || navigator.userLanguage || '';
  const langCode = browserLang.toLowerCase();
  
  // German: de, de-DE, de-AT, de-CH
  if (langCode.startsWith('de') || langCode.startsWith('at') || langCode.startsWith('ch')) {
    return 'de';
  }
  
  // Arabic: ar, ar-SA, ar-EG, ar-AE, etc.
  if (langCode.startsWith('ar')) {
    return 'ar';
  }
  
  // Default to English for all other languages
  return 'en';
};

// Get initial language from localStorage or detect from browser
const getInitialLanguage = () => {
  // Check if user has manually selected a language previously
  const savedLanguage = localStorage.getItem(LANGUAGE_STORAGE_KEY);
  
  if (savedLanguage && ['en', 'de', 'ar'].includes(savedLanguage)) {
    // Respect user's manual selection
    return savedLanguage;
  }
  
  // No manual selection exists, auto-detect from browser
  const detectedLanguage = detectBrowserLanguage();
  
  // Save the auto-detected preference
  localStorage.setItem(LANGUAGE_STORAGE_KEY, detectedLanguage);
  
  return detectedLanguage;
};

const LandingPage = () => {
  const [language, setLanguageState] = useState(getInitialLanguage);
  const [visibleSections, setVisibleSections] = useState(new Set());
  const [showImprint, setShowImprint] = useState(false);
  const [showPrivacy, setShowPrivacy] = useState(false);

  // Wrapper for setLanguage that also saves to localStorage (manual selection)
  const setLanguage = useCallback((newLang) => {
    localStorage.setItem(LANGUAGE_STORAGE_KEY, newLang);
    setLanguageState(newLang);
  }, []);

  /* Set HTML lang/dir for accessibility + RTL */

  useEffect(() => {
    document.documentElement.lang = language;
    document.documentElement.dir = language === 'ar' ? 'rtl' : 'ltr';
  }, [language]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setVisibleSections((prev) => new Set([...prev, entry.target.id]));
          }
        });
      },
      { threshold: 0.1 }
    );

    document.querySelectorAll('section[id]').forEach((section) => {
      observer.observe(section);
    });

    return () => observer.disconnect();
  }, []);

  const t = translations[language];

  return (
    <div className="landing-page">
      <Header language={language} setLanguage={setLanguage} t={t} />
      <Hero t={t} isVisible={visibleSections.has('hero')} language={language} />
      <Services t={t} isVisible={visibleSections.has('services')} />
      <WhyPartner t={t} isVisible={visibleSections.has('why-partner')} />
      <Contact t={t} isVisible={visibleSections.has('contact')} language={language} />
      <Footer 
        t={t} 
        onShowImprint={() => setShowImprint(true)}
        onShowPrivacy={() => setShowPrivacy(true)}
      />
      
      {/* Modals */}
      <ImprintModal 
        isOpen={showImprint} 
        onClose={() => setShowImprint(false)} 
        t={t}
        isArabic={language === 'ar'}
      />
      <PrivacyModal 
        isOpen={showPrivacy} 
        onClose={() => setShowPrivacy(false)} 
        t={t} 
      />
    </div>
  );
};

export default LandingPage;